import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Section tabs that are real links, not local state.
 *
 * The distinction matters here: a tab that only lives in `useState` cannot be
 * bookmarked, cannot be sent to a colleague, loses its place on refresh, and
 * breaks the browser's back button. Settings is exactly the kind of screen
 * people link each other to ("the branding tab"), so each tab owns a URL.
 *
 * Visually this is the underline treatment from the reference: the active tab
 * is brand-coloured with a 2px rule directly under it, sitting on a full-width
 * hairline that separates the tab row from the panel below.
 *
 * Use `<Tabs>` (in Feedback.tsx) instead when the choice is a filter within
 * one screen rather than a section of it — a filter does not deserve a URL.
 */
export interface LinkTabItem {
  to: string;
  label: string;
  /** Marks the tab that also matches the parent path (the default section). */
  end?: boolean;
}

export function LinkTabs({
  items,
  trailing,
}: {
  items: readonly LinkTabItem[];
  /** Panel actions, rendered at the right-hand end of the same row. */
  trailing?: ReactNode;
}) {
  /*
   * The border lives on the wrapper and the scrolling happens on the <nav>,
   * with `overflow-y` pinned to hidden.
   *
   * Both details are load-bearing. Setting only `overflow-x: auto` does not
   * leave the other axis alone: per the CSS overflow spec, when one axis is
   * not `visible` the other computes from `visible` to `auto`. So the tab row
   * silently gained `overflow-y: auto`, and because the active tab is pulled
   * down a pixel (`-mb-px`) to sit on the rule, content was exactly 1px taller
   * than the box. One pixel of overflow renders a full-height scrollbar.
   *
   * The end-buttons appear only when there is somewhere to scroll, and they
   * ride outside the <nav> so the action slot can never be mistaken for a
   * tab — the same confusion the vertical rule was added to fix.
   */
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = (): void => {
      setOverflow({
        left: el.scrollLeft > 2,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [items.length]);

  function nudge(direction: -1 | 1): void {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  }

  return (
    <div className="flex shrink-0 items-center gap-1 border-b border-rule">
      {overflow.left ? (
        <button
          aria-label="Scroll tabs left"
          className="grid h-9 w-8 shrink-0 place-items-center rounded-md text-muted hover:bg-ink/[0.04] hover:text-ink"
          onClick={() => nudge(-1)}
          type="button"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
        </button>
      ) : null}
      <nav
        className="-mb-px flex min-w-0 flex-1 gap-5 gq-no-scrollbar overflow-x-auto overflow-y-hidden"
        ref={scrollerRef}
      >
        {items.map((item) => (
          <NavLink
            className={({ isActive }) =>
              [
                // The nav itself carries the -mb-px, so each tab's underline
                // lands on the wrapper's rule and the two read as one line.
                "border-b-2 pt-2 pb-2.5 font-title text-[13px] font-medium whitespace-nowrap transition-colors",
                isActive
                  ? "border-brand text-ink"
                  : "border-transparent text-muted hover:border-ink/20 hover:text-ink",
              ].join(" ")
            }
            end={item.end}
            key={item.to}
            to={item.to}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      {overflow.right ? (
        <button
          aria-label="Scroll tabs right"
          className="grid h-9 w-8 shrink-0 place-items-center rounded-md text-muted hover:bg-ink/[0.04] hover:text-ink"
          onClick={() => nudge(1)}
          type="button"
        >
          <ChevronRight aria-hidden="true" className="size-4" />
        </button>
      ) : null}
      {trailing ? (
        /*
         * The rule keeps the actions from sitting flush against the last tab
         * and reading as more tabs — which is exactly what a row of bare
         * colour swatches next to "WhatsApp" looked like.
         *
         * `:has(> div:empty)` hides the whole thing when the portal target has
         * no content. The slot element always exists (a panel needs something
         * to portal INTO), so without this a tab with no actions still drew a
         * short vertical line floating at the end of the row.
         */
        <div className="relative flex shrink-0 items-center gap-3 pb-1.5 pl-4 before:absolute before:left-0 before:h-5 before:w-px before:bg-rule before:content-[''] [&:has(>div:empty)]:hidden">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
